/**
 * Termine eines Projekts, Antrags oder Nachweises samt Pflege der manuellen Fristen (ADR 010).
 *
 * Jede Änderung lädt die Liste neu, statt sie lokal nachzubauen: Ob eine Frist nach dem Ändern
 * noch zum Bezug gehört und wie der Server ihn benennt, weiß nur der Server.
 */
import { useCallback, useEffect, useState } from "react";
import {
  deadlinesApi,
  DeadlineReferenceFilter,
} from "../services/api/deadlines";
import {
  DeadlineItem,
  DeadlineSubmission,
  ManualDeadlineInput,
} from "../types";
import { useToast } from "../contexts/ToastContext";
import { useLoadState } from "./useLoadState";

export const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

export function useProjectDeadlines(
  projectId: string | undefined,
  filter: DeadlineReferenceFilter = {},
) {
  const { permitProcedureId, evidenceId } = filter;
  const [deadlines, setDeadlines] = useState<DeadlineItem[]>([]);
  const loadKey = `${projectId}|${permitProcedureId ?? ""}|${evidenceId ?? ""}`;
  const { loading, ladenStarten, ladenBeenden } = useLoadState(loadKey);
  const { showError, showSuccess } = useToast();

  const reload = useCallback(async () => {
    if (!projectId) return;
    ladenStarten();
    try {
      setDeadlines(
        await deadlinesApi.getProjectDeadlines(projectId, {
          permitProcedureId,
          evidenceId,
        }),
      );
    } catch (err) {
      showError(errorMessage(err, "Termine konnten nicht geladen werden"));
    } finally {
      ladenBeenden();
    }
  }, [
    projectId,
    permitProcedureId,
    evidenceId,
    ladenStarten,
    ladenBeenden,
    showError,
  ]);

  useEffect(() => {
    reload();
  }, [reload]);

  /** Führt eine Änderung aus und lädt neu; `false`, wenn der Server sie abgelehnt hat */
  const mutate = useCallback(
    async (action: () => Promise<unknown>, fallback: string) => {
      try {
        await action();
        await reload();
        return true;
      } catch (err) {
        showError(errorMessage(err, fallback));
        return false;
      }
    },
    [reload, showError],
  );

  const create = useCallback(
    (data: ManualDeadlineInput) =>
      mutate(
        () => deadlinesApi.createManualDeadline(projectId!, data),
        "Termin konnte nicht angelegt werden",
      ),
    [mutate, projectId],
  );

  const update = useCallback(
    (deadlineId: string, data: ManualDeadlineInput) =>
      mutate(
        () => deadlinesApi.updateManualDeadline(projectId!, deadlineId, data),
        "Termin konnte nicht gespeichert werden",
      ),
    [mutate, projectId],
  );

  const setDone = useCallback(
    (deadlineId: string, done: boolean) =>
      mutate(
        () => deadlinesApi.setManualDeadlineDone(projectId!, deadlineId, done),
        "Termin konnte nicht abgehakt werden",
      ),
    [mutate, projectId],
  );

  const remove = useCallback(
    (deadlineId: string) =>
      mutate(
        () => deadlinesApi.deleteManualDeadline(projectId!, deadlineId),
        "Termin konnte nicht gelöscht werden",
      ),
    [mutate, projectId],
  );

  /**
   * Speichert, was der Dialog „Termin anlegen“ liefert. Ein persönlicher Termin gehört in
   * keine Projektliste – er erscheint nur in den Terminen der Startseite, daher die Meldung.
   */
  const submit = useCallback(
    async (submission: DeadlineSubmission): Promise<boolean> => {
      if (submission.kind === "project") return create(submission.data);
      try {
        await deadlinesApi.createPersonalDeadline(submission.data);
        showSuccess(
          "Persönlicher Termin angelegt – er steht in den Terminen der Startseite",
        );
        return true;
      } catch (err) {
        showError(errorMessage(err, "Termin konnte nicht angelegt werden"));
        return false;
      }
    },
    [create, showError, showSuccess],
  );

  return {
    deadlines,
    loading,
    reload,
    create,
    update,
    setDone,
    remove,
    submit,
  };
}
